import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { requerimientoService, datosGeneralesService } from '../services/api';
import { getErrorDetail } from '../utils/errorHelper';
import '../styles/datatable.css';

const fmtNum = (v) => (parseFloat(v) || 0).toLocaleString('es-PE', { maximumFractionDigits: 4 });

const TABS = [
  { key: 'oc',         label: 'Orden de Compra',        color: '#60a5fa', rowsKey: 'ocs',         empty: 'Sin OCs para este artículo' },
  { key: 'pe_origen',  label: 'PE Otros Almacenes',     color: '#22c55e', rowsKey: 'pes_origen',  empty: 'Sin PEs en otros almacenes' },
  { key: 'gr',         label: 'Guías de Remisión',      color: '#a78bfa', rowsKey: 'grs',         empty: 'Sin guías de remisión' },
  { key: 'pe_destino', label: 'PE Almacén Solicitante', color: '#fb923c', rowsKey: 'pes_destino', empty: 'Sin PEs en el almacén solicitante' },
];

const ESTADO_DOC_MAP = {
  PENDIENTE:   { color: '#f59e0b', bg: 'rgba(245,158,11,0.12)' },
  APROBADO:    { color: '#22c55e', bg: 'rgba(34,197,94,0.12)'  },
  DESAPROBADO: { color: '#fb923c', bg: 'rgba(251,146,60,0.12)' },
  ANULADO:     { color: '#64748b', bg: 'rgba(100,116,139,0.12)'},
  GENERADO:    { color: '#60a5fa', bg: 'rgba(96,165,250,0.12)' },
  TRASLADADO:  { color: '#22c55e', bg: 'rgba(34,197,94,0.12)'  },
};
const EstadoBadge = ({ estado }) => {
  const c = ESTADO_DOC_MAP[estado] || { color: '#94a3b8', bg: 'rgba(148,163,184,0.1)' };
  return <span style={{ background: c.bg, color: c.color, padding: '2px 7px', borderRadius: 6, fontSize: 10, fontWeight: 700 }}>{estado || '—'}</span>;
};

const ESTADO_ATN_MAP = {
  PENDIENTE: { color: '#f59e0b', bg: 'rgba(245,158,11,0.12)', border: 'rgba(245,158,11,0.3)' },
  PARCIAL:   { color: '#60a5fa', bg: 'rgba(96,165,250,0.12)', border: 'rgba(96,165,250,0.3)' },
  ATENDIDO:  { color: '#22c55e', bg: 'rgba(34,197,94,0.12)',  border: 'rgba(34,197,94,0.3)'  },
};
const EstadoAtencionBadge = ({ estado }) => {
  const c = ESTADO_ATN_MAP[estado] || { color: '#94a3b8', bg: 'rgba(148,163,184,0.1)', border: 'rgba(148,163,184,0.2)' };
  return <span style={{ background: c.bg, color: c.color, padding: '3px 10px', borderRadius: 8, fontSize: 11, fontWeight: 700, border: `1px solid ${c.border}`, whiteSpace: 'nowrap' }}>{estado || '—'}</span>;
};

const buildDocCols = (tabKey) => {
  if (tabKey === 'oc') return [
    { label: 'N°', render: r => <span style={{ color: '#60a5fa', fontWeight: 700 }}>{r.serie_codigo ? `${r.serie_codigo}-${r.numero}` : `OC-${r.numero}`}</span>, w: 120 },
    { label: 'Fecha', render: r => (r.fecha_emision || '').slice(0, 10), w: 100 },
    { label: 'Proveedor', render: r => r.proveedor_nombre || '—' },
    { label: 'RUC', render: r => r.proveedor_ruc || '—', w: 110 },
    { label: 'Estado', render: r => <EstadoBadge estado={r.estado} />, w: 100 },
    { label: 'Cantidad', render: r => <span style={{ fontWeight: 700, color: '#e2e8f0' }}>{fmtNum(r.cantidad)}</span>, w: 100, align: 'right' },
    { label: 'Precio', render: r => {
      const p = r.moneda === 'USD' ? r.precio_dolares : r.precio_soles;
      const sym = r.moneda === 'USD' ? 'US$' : 'S/';
      return <span style={{ color: '#94a3b8' }}>{sym} {fmtNum(p)}</span>;
    }, w: 110, align: 'right' },
    { label: 'Observación', render: r => <span style={{ color: '#64748b', fontSize: 11 }}>{r.observacion || '—'}</span> },
  ];
  if (tabKey === 'pe_origen' || tabKey === 'pe_destino') return [
    { label: 'N°', render: r => <span style={{ color: tabKey === 'pe_origen' ? '#22c55e' : '#fb923c', fontWeight: 700 }}>PE-{r.numero}</span>, w: 100 },
    { label: 'Fecha', render: r => (r.fecha_operacion || '').slice(0, 10), w: 100 },
    { label: 'Almacén', render: r => r.almacen_nombre || '—' },
    { label: 'Sucursal', render: r => r.sucursal_nombre || '—', w: 140 },
    { label: 'Origen', render: r =>
      r.oc_numero ? <span style={{ color: '#60a5fa', fontSize: 11 }}>{r.oc_serie ? `${r.oc_serie}-` : 'OC-'}{r.oc_numero}</span>
      : r.gr_numero ? <span style={{ color: '#a78bfa', fontSize: 11 }}>{r.gr_serie ? `${r.gr_serie}-` : 'GR-'}{r.gr_numero}</span>
      : '—', w: 120 },
    { label: 'Estado', render: r => <EstadoBadge estado={r.estado} />, w: 100 },
    { label: 'Cantidad', render: r => <span style={{ fontWeight: 700, color: '#e2e8f0' }}>{fmtNum(r.cantidad)}</span>, w: 100, align: 'right' },
    { label: 'Observación', render: r => <span style={{ color: '#64748b', fontSize: 11 }}>{r.observacion || '—'}</span> },
  ];
  if (tabKey === 'gr') return [
    { label: 'N°', render: r => <span style={{ color: '#a78bfa', fontWeight: 700 }}>{r.serie_codigo ? `${r.serie_codigo}-${r.numero}` : `GR-${r.numero}`}</span>, w: 120 },
    { label: 'Fecha Guía', render: r => (r.fecha_guia || '').slice(0, 10), w: 110 },
    { label: 'Fecha Traslado', render: r => (r.fecha_traslado || '').slice(0, 10), w: 120 },
    { label: 'Origen', render: r => r.almacen_origen_nombre || '—' },
    { label: 'Destino', render: r => r.almacen_destino_nombre || '—' },
    { label: 'Estado', render: r => <EstadoBadge estado={r.estado} />, w: 110 },
    { label: 'Cantidad', render: r => <span style={{ fontWeight: 700, color: '#e2e8f0' }}>{fmtNum(r.cantidad)}</span>, w: 100, align: 'right' },
    { label: 'Observación', render: r => <span style={{ color: '#64748b', fontSize: 11 }}>{r.observacion || '—'}</span> },
  ];
  return [];
};

const ArticuloGroup = ({ item, docs, docCols, color, emptyText }) => {
  const solicitada = parseFloat(item.cantidad_solicitada) || 0;
  const recibida   = parseFloat(item.cantidad_recibida)   || 0;
  const enOc       = parseFloat(item.cantidad_oc)         || 0;
  const saldo      = parseFloat(item.saldo_pendiente)     || Math.max(solicitada - recibida, 0);

  return (
    <div style={{
      marginBottom: 22, border: '1px solid rgba(255,255,255,0.07)', borderRadius: 12,
      overflow: 'hidden', background: 'rgba(15,27,45,0.4)',
    }}>
      <div style={{
        padding: '14px 18px',
        background: 'linear-gradient(90deg, rgba(15,27,45,0.85), rgba(15,27,45,0.4))',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        borderLeft: `4px solid ${color}`,
      }}>
        <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 340px', minWidth: 0 }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 6 }}>
              {item.articulo_codigo && (
                <span style={{ fontFamily: 'monospace', fontSize: 11, padding: '2px 8px', background: 'rgba(96,165,250,0.14)', color: '#93c5fd', borderRadius: 5, fontWeight: 700 }}>
                  {item.articulo_codigo}
                </span>
              )}
              <span style={{ fontWeight: 600, color: '#e2e8f0', fontSize: 14.5 }}>
                {item.articulo_descripcion || '—'}
              </span>
              {item.unidad_descripcion && (
                <span style={{ fontSize: 11.5, color: '#94a3b8' }}>
                  · {item.unidad_descripcion}{item.unidad_codigo ? ` (${item.unidad_codigo})` : ''}
                </span>
              )}
            </div>
            <div style={{ display: 'flex', gap: 18, fontSize: 11.5, color: '#94a3b8', flexWrap: 'wrap' }}>
              {item.centro_costo_descripcion && (
                <span><span style={{ color: '#64748b' }}>CC:</span> {item.centro_costo_codigo ? `${item.centro_costo_codigo} · ` : ''}{item.centro_costo_descripcion}</span>
              )}
              {item.observacion && (
                <span><span style={{ color: '#64748b' }}>Obs:</span> <span style={{ fontStyle: 'italic' }}>{item.observacion}</span></span>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 20, alignItems: 'center', flexShrink: 0 }}>
            <MetricCell label="Solicitado" value={solicitada} color="#e2e8f0" />
            <MetricCell label="En OC"      value={enOc}       color={enOc > 0 ? '#60a5fa' : '#475569'} />
            <MetricCell label="Recibido"   value={recibida}   color={recibida > 0 ? '#22c55e' : '#475569'} />
            <MetricCell label="Saldo"      value={saldo}      color={saldo > 0 ? '#f59e0b' : '#22c55e'} />
            <EstadoAtencionBadge estado={item.estado_atencion} />
          </div>
        </div>
      </div>

      {docs.length === 0 ? (
        <div style={{ padding: '14px 18px', fontSize: 12, color: '#64748b', fontStyle: 'italic' }}>
          {emptyText}
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table" style={{ width: '100%', fontSize: 12.5, margin: 0 }}>
            <thead>
              <tr>
                {docCols.map((c, i) => (
                  <th key={i} style={{ width: c.w, textAlign: c.align || 'left' }}>{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {docs.map((d, i) => (
                <tr key={i}>
                  {docCols.map((c, j) => (
                    <td key={j} style={{ textAlign: c.align || 'left' }}>{c.render(d)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const MetricCell = ({ label, value, color }) => (
  <div style={{ textAlign: 'right', minWidth: 70 }}>
    <div style={{ fontSize: 9.5, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.6 }}>{label}</div>
    <div style={{ fontSize: 15, fontWeight: 700, color }}>{fmtNum(value)}</div>
  </div>
);

const SeguimientoDetalladoRQ = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('oc');
  const [filtroArt, setFiltroArt] = useState('');
  const [soloConMov, setSoloConMov] = useState(false);
  const [empresa, setEmpresa] = useState(null);

  useEffect(() => {
    datosGeneralesService.get()
      .then(r => setEmpresa(r.data || null))
      .catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError(null);
      try {
        const res = await requerimientoService.getSeguimientoDetallado(id);
        if (!cancelled) setData(res.data);
      } catch (err) {
        if (!cancelled) setError(getErrorDetail(err, 'No se pudo cargar el seguimiento.'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const counts = useMemo(() => ({
    oc:         (data?.ocs || []).length,
    pe_origen:  (data?.pes_origen || []).length,
    gr:         (data?.grs || []).length,
    pe_destino: (data?.pes_destino || []).length,
  }), [data]);

  const rq = data?.requerimiento;
  const activeTab = TABS.find(t => t.key === tab) || TABS[0];
  const rowsAll = data ? (data[activeTab.rowsKey] || []) : [];
  const docCols = useMemo(() => buildDocCols(tab), [tab]);

  const itemsFiltered = useMemo(() => {
    const items = data?.items || [];
    const q = filtroArt.trim().toLowerCase();
    return items.filter(it => {
      if (q) {
        const hay = `${it.articulo_codigo || ''} ${it.articulo_descripcion || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (soloConMov) {
        const docs = rowsAll.filter(r => r.id_detalle === it.id_detalle);
        if (docs.length === 0) return false;
      }
      return true;
    });
  }, [data, filtroArt, soloConMov, rowsAll]);

  return (
    <Layout title="Seguimiento Detallado" subtitle={rq ? `${rq.serie_codigo || 'RQ'}-${rq.numero}` : `RQ #${id}`}>
      <div style={{ padding: '0 4px' }}>
        {/* Cabecera print-only con logo de la empresa */}
        <div className="print-only" style={{
          display: 'none', alignItems: 'center', gap: 16, padding: '12px 4px 16px',
          borderBottom: '2px solid #1e3a5f', marginBottom: 16, color: '#1a1a2e',
        }}>
          {empresa?.logo_base64 && (
            <img src={empresa.logo_base64} alt="Logo" style={{ maxHeight: 64, maxWidth: 140, objectFit: 'contain' }} />
          )}
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#1e3a5f' }}>{empresa?.razon_social || ''}</div>
            {empresa?.ruc_empresa && <div style={{ fontSize: 10, color: '#555' }}>RUC: {empresa.ruc_empresa}</div>}
            <div style={{ fontSize: 12, color: '#333', marginTop: 4, fontWeight: 600 }}>
              Seguimiento Detallado — {rq ? `${rq.serie_codigo || 'RQ'}-${rq.numero}` : `RQ #${id}`}
            </div>
          </div>
          <div style={{ fontSize: 10, color: '#666' }}>{new Date().toLocaleString('es-PE')}</div>
        </div>
        <style>{`@media print { .print-only { display: flex !important; } }`}</style>

        {/* Cabecera */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginBottom: 16, padding: '14px 18px', borderRadius: 12,
          background: 'rgba(15,27,45,0.6)', border: '1px solid rgba(255,255,255,0.06)',
        }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#e2e8f0' }}>
              {rq ? `${rq.serie_codigo || 'RQ'}-${rq.numero}` : `RQ #${id}`}
            </div>
            {rq && (
              <div style={{ display: 'flex', gap: 16, fontSize: 12, color: '#94a3b8', marginTop: 4, flexWrap: 'wrap' }}>
                {rq.fecha_emision && <span><span style={{ color: '#64748b' }}>Fecha:</span> {String(rq.fecha_emision).slice(0, 10)}</span>}
                {rq.sucursal_nombre && <span><span style={{ color: '#64748b' }}>Sucursal:</span> {rq.sucursal_nombre}</span>}
                {rq.estado && <span><EstadoBadge estado={rq.estado} /></span>}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-secondary" onClick={() => navigate(-1)}>← Volver</button>
            <button className="btn btn-secondary" onClick={() => window.print()} title="Imprimir reporte">Imprimir</button>
          </div>
        </div>

        {/* Filtros */}
        <div style={{
          display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap',
          marginBottom: 14, padding: '10px 14px', borderRadius: 10,
          background: 'rgba(15,27,45,0.4)', border: '1px solid rgba(255,255,255,0.05)',
        }}>
          <input
            type="text"
            value={filtroArt}
            onChange={e => setFiltroArt(e.target.value)}
            placeholder="Buscar por código o descripción del artículo..."
            style={{
              flex: '1 1 280px', padding: '8px 12px', borderRadius: 8,
              background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)',
              color: '#e2e8f0', fontSize: 13, outline: 'none',
            }}
          />
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: '#94a3b8', cursor: 'pointer', userSelect: 'none' }}>
            <input type="checkbox" checked={soloConMov} onChange={e => setSoloConMov(e.target.checked)} />
            Solo artículos con movimientos en esta sección
          </label>
          <div style={{ fontSize: 12, color: '#64748b' }}>
            {itemsFiltered.length} de {(data?.items || []).length} artículos
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 14, borderBottom: '1px solid rgba(255,255,255,0.08)', overflowX: 'auto' }}>
          {TABS.map(t => {
            const n = counts[t.key];
            const active = tab === t.key;
            return (
              <button key={t.key} type="button" onClick={() => setTab(t.key)}
                style={{
                  padding: '10px 16px', background: active ? 'rgba(15,27,45,0.8)' : 'transparent',
                  border: 'none', borderBottom: active ? `2px solid ${t.color}` : '2px solid transparent',
                  color: active ? '#e2e8f0' : '#94a3b8', fontSize: 13, fontWeight: active ? 600 : 500,
                  cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.15s ease',
                  display: 'flex', alignItems: 'center', gap: 8,
                }}>
                <span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: '50%', background: t.color }} />
                {t.label}
                <span style={{ padding: '1px 7px', fontSize: 10, borderRadius: 10, background: n > 0 ? t.color : 'rgba(148,163,184,0.15)', color: n > 0 ? '#0f1b2d' : '#64748b', fontWeight: 700 }}>{n}</span>
              </button>
            );
          })}
        </div>

        {/* Contenido */}
        {loading ? (
          <div style={{ padding: 80, textAlign: 'center', color: '#64748b' }}>Cargando trazabilidad detallada...</div>
        ) : error ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#f87171' }}>{error}</div>
        ) : itemsFiltered.length === 0 ? (
          <div style={{ padding: 60, textAlign: 'center', color: '#64748b' }}>
            {(data?.items || []).length === 0 ? 'El requerimiento no tiene líneas' : 'Sin resultados para el filtro aplicado'}
          </div>
        ) : (
          <div>
            {itemsFiltered.map(it => {
              const docs = rowsAll.filter(r => r.id_detalle === it.id_detalle);
              return (
                <ArticuloGroup
                  key={it.id_detalle}
                  item={it}
                  docs={docs}
                  docCols={docCols}
                  color={activeTab.color}
                  emptyText={activeTab.empty}
                />
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default SeguimientoDetalladoRQ;
